// src/pages/Review.tsx
import { useState, useEffect, useCallback } from 'react'
import { Flame, Zap, AlertTriangle, RefreshCw } from 'lucide-react'
import { supabase } from '../services/supabase'
import { api } from '../services/api'
import { useSNR } from '../stores/useSNR'
import { useTasks } from '../stores/useTasks'
import { useSettings } from '../stores/useSettings'

// ── Question bank ──
const QUESTION_BANK = [
  { q: 'What made today work? How do you replicate it tomorrow?',            tiers: ['high'] },
  { q: 'Which signal task gave you the most energy today?',                  tiers: ['high'] },
  { q: 'What would you protect more of tomorrow?',                           tiers: ['high', 'mid', 'low'] },
  { q: "Did your time estimates match reality today?",                        tiers: ['high', 'mid', 'low'] },
  { q: "Which noise source ate the most time today — and what's your plan?", tiers: ['mid', 'low'] },
  { q: 'What one recurring distraction could you cut permanently?',          tiers: ['mid', 'low'] },
  { q: "Was anything you worked on today not on your signal list?",           tiers: ['high', 'mid', 'low'] },
  { q: 'If you repeated today, what one thing would you change?',            tiers: ['high', 'mid', 'low'] },
  { q: 'Who or what pulled you furthest from your signals today?',           tiers: ['mid', 'low'] },
  { q: "What must you protect tomorrow to hit 75% SNR?",                     tiers: ['low', 'mid'] },
] as const

function getQuestion(snrPct: number): string {
  const tier = snrPct >= 75 ? 'high' : snrPct >= 50 ? 'mid' : 'low'
  const eligible = QUESTION_BANK.filter(q => (q.tiers as readonly string[]).includes(tier))
  return eligible[Math.floor(Math.random() * eligible.length)]?.q ?? QUESTION_BANK[0].q
}

const NOISE_KEYWORDS = ['slack','email','meeting','calls','twitter','reddit','youtube','social','browsing','news','notifications','chat','whatsapp','instagram','tiktok']

function detectNoisePattern(reflections: string[]): string | null {
  if (reflections.length < 3) return null
  const counts: Record<string, number> = {}
  for (const ref of reflections) {
    const lower = ref.toLowerCase(); const seen = new Set<string>()
    for (const kw of NOISE_KEYWORDS) if (lower.includes(kw) && !seen.has(kw)) { counts[kw] = (counts[kw] || 0) + 1; seen.add(kw) }
  }
  const flagged = Object.entries(counts).filter(([,n]) => n >= 3).sort((a,b) => b[1] - a[1])
  if (!flagged.length) return null
  const [kw, count] = flagged[0]
  return `You've mentioned "${kw}" as a noise source in ${count} of your last ${reflections.length} reflections. Consider adding a deep-work block as a recurring Signal to crowd it out.`
}

function formatNoiseTime(secs: number): string {
  const h = Math.floor(secs / 3600); const m = Math.floor((secs % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`; return `${m}m`
}

function todayKey()     { return new Date().toISOString().split('T')[0] }
function yesterdayKey() { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().split('T')[0] }
function getMonday(d: Date): string {
  const day = d.getDay(); const diff = (day === 0 ? -6 : 1) - day; const mon = new Date(d); mon.setDate(d.getDate() + diff)
  return mon.toISOString().split('T')[0]
}

// ── SNR Chart ──
function SNRChart({ data }: { data: Array<{ date: string; value: number }> }) {
  if (!data.length) return <div className="empty-state" style={{ padding: 'var(--xl)' }}><p className="body-md text-dim">No data yet</p></div>
  const max = Math.max(...data.map(d => d.value), 1)
  return (
    <div className="chart-bar-wrap">
      {data.map(d => {
        const color = d.value >= 75 ? 'var(--signal)' : d.value >= 50 ? 'var(--amber)' : 'var(--noise)'
        return (
          <div key={d.date} className="chart-bar" style={{ background: color, height: `${Math.max(4, (d.value / max) * 100)}%` }}>
            <div className="chart-bar-tooltip">{d.date.slice(5)}: {d.value}%</div>
          </div>
        )
      })}
    </div>
  )
}

export default function ReviewPage() {
  const { snrPct, signalCount, noiseCount, doneCount, noiseTimerSeconds } = useSNR()
  const { tasks } = useTasks()
  const { settings } = useSettings()

  const [reviews, setReviews] = useState<{
    date: string; snr_pct: number; reflection_answer?: string; coaching_note?: string;
    signals_count?: number; signals_done?: number; noises_count?: number; noise_seconds?: number;
  }[]>([])
  const [reflection, setReflection]     = useState('')
  const [coachNote, setCoachNote]       = useState('')
  const [coachLoading, setCoachLoading] = useState(false)
  const [noisePattern, setNoisePattern] = useState<string | null>(null)
  const [chartRange, setChartRange]     = useState<7 | 30>(7)
  const [weekSynth, setWeekSynth]       = useState('')
  const [weekLoading, setWeekLoading]   = useState(false)
  const [saving, setSaving]             = useState(false)
  const [saveStatus, setSaveStatus]     = useState<'idle' | 'saved' | 'error'>('idle')

  // Derive question directly (no effect needed)
  const question = getQuestion(snrPct)

  // Load reviews once
  useEffect(() => {
    supabase.from('daily_reviews').select('*').order('date', { ascending: true }).limit(31)
      .then(({ data }) => {
        if (data) {
          setReviews(data)
          // Restore today's saved answer at load time (inside async callback, not synchronously in effect body)
          const today = data.find((r: { date: string; reflection_answer?: string; coaching_note?: string }) => r.date === todayKey())
          if (today?.reflection_answer) setReflection(today.reflection_answer)
          if (today?.coaching_note) setCoachNote(today.coaching_note)
        }
      })
  }, [])

  const generateWeekReview = useCallback(async (weekStart: string, userId: string) => {
    setWeekLoading(true)
    try {
      const today = new Date()
      const days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(); d.setDate(today.getDate() - (6 - i))
        const dateStr = d.toISOString().split('T')[0]
        const row = reviews.find(r => r.date === dateStr)
        return { date: dateStr, snr_pct: i === 6 ? snrPct : (row?.snr_pct ?? 0), reflection_answer: row?.reflection_answer ?? null }
      })
      const result = await api.getWeekReview(days)
      const synthesis = result.synthesis ?? result.text ?? result
      const text = typeof synthesis === 'string' ? synthesis : JSON.stringify(synthesis)
      setWeekSynth(text)
      await supabase.from('weekly_reviews').upsert({ user_id: userId, week_start: weekStart, synthesis: text })
    } catch { setWeekSynth('Could not generate Week in Review. Check your connection.') }
    finally { setWeekLoading(false) }
  }, [reviews, snrPct])

  // Week in review (pro)
  useEffect(() => {
    if (!settings.is_premium) return
    const weekStart = getMonday(new Date())
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      supabase.from('weekly_reviews').select('synthesis').eq('user_id', user.id).eq('week_start', weekStart).maybeSingle()
        .then(({ data: cached }) => {
          if (cached?.synthesis) { setWeekSynth(cached.synthesis); return }
          generateWeekReview(weekStart, user.id)
        })
    })
  }, [settings.is_premium, generateWeekReview])

  const handleSave = async () => {
    setSaving(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')
      const date = todayKey()
      const { error } = await supabase.from('daily_reviews').upsert({
        user_id: session.user.id, date, snr_pct: snrPct, signals_count: signalCount,
        signals_done: doneCount, noises_count: noiseCount, noise_seconds: noiseTimerSeconds,
        reflection_answer: reflection, coaching_note: coachNote || null,
      }, { onConflict: 'user_id,date' })
      if (error) throw error
      // Noise pattern detection
      const allAnswers = reviews.filter(r => r.reflection_answer?.trim()).map(r => r.reflection_answer as string).slice(0, 6)
      if (reflection.trim()) allAnswers.unshift(reflection)
      setNoisePattern(detectNoisePattern(allAnswers.slice(0, 7)))
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus('idle'), 2500)
    } catch { setSaveStatus('error'); setTimeout(() => setSaveStatus('idle'), 2500) }
    finally { setSaving(false) }
  }

  const handleGenerateCoaching = useCallback(async () => {
    setCoachLoading(true); setCoachNote('')
    try {
      const result = await api.getCoachNote({
        snrPct,
        signalTasks: tasks.filter(t => t.type === 'signal').map(t => t.text),
        noiseTasks: tasks.filter(t => t.type === 'noise').map(t => t.text),
        noiseSeconds: noiseTimerSeconds,
        signalsDone: doneCount,
        pastReflections: reviews.filter(r => r.reflection_answer?.trim()).slice(0, 5).map(r => r.reflection_answer as string).reverse(),
        coaching_tone: settings.coaching_tone,
      })
      const note = result.coaching ?? result.note ?? result.text ?? result
      setCoachNote(typeof note === 'string' ? note : JSON.stringify(note))
    } catch { alert('AI coach unavailable. Check your connection and API key.') }
    finally { setCoachLoading(false) }
  }, [tasks, reviews, snrPct, noiseTimerSeconds, doneCount, settings.coaching_tone])

  const reflectionStreak = (() => {
    let streak = 0; const today = new Date()
    for (let i = 1; i <= 30; i++) {
      const d = new Date(today); d.setDate(today.getDate() - i)
      const row = reviews.find(r => r.date === d.toISOString().split('T')[0])
      if (row?.reflection_answer?.trim()) streak++; else break
    }
    return streak
  })()

  const yesterday = reviews.find(r => r.date === yesterdayKey())
  const chartData = reviews.slice(-chartRange).map(r => ({ date: r.date, value: r.snr_pct || 0 }))

  return (
    <div className="page page-top">
      {saveStatus !== 'idle' && (
        <div className={`toast ${saveStatus === 'saved' ? 'toast-success' : 'toast-error'}`}>
          {saveStatus === 'saved' ? '✓  Saved' : '✕  Save failed'}
        </div>
      )}

      <h1 className="display-md text-primary" style={{ marginBottom: 'var(--xl)' }}>Review</h1>

      {/* Stat cards */}
      <div className="h-scroll" style={{ marginBottom: 'var(--xl)' }}>
        {[
          { label: "Today's SNR",  value: `${snrPct}%` },
          { label: 'Signals Done', value: doneCount },
          { label: 'Noise Count',  value: noiseCount },
          { label: 'Noise Time',   value: formatNoiseTime(noiseTimerSeconds) },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-card-value text-primary">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Yesterday's reflection */}
      {yesterday?.reflection_answer && (
        <div className="card animate-fade-slide-up" style={{ marginBottom: 'var(--xl)', borderLeft: '3px solid var(--accent)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sm)', marginBottom: 'var(--xs)' }}>
            <span className="label text-accent">YESTERDAY</span>
            <span className="label text-dim">{yesterday.date} · {yesterday.snr_pct ?? '?'}% SNR</span>
          </div>
          <p className="body-md text-muted" style={{ fontStyle: 'italic', lineHeight: 1.55 }}>"{yesterday.reflection_answer}"</p>
        </div>
      )}

      {/* SNR Chart */}
      <div className="card" style={{ marginBottom: 'var(--xl)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--md)' }}>
          <span className="label text-muted">SNR Trend</span>
          <div style={{ display: 'flex', gap: 'var(--sm)' }}>
            {([7, 30] as const).map(d => (
              <button key={d}
                className={`badge ${chartRange === d ? 'badge-accent' : 'badge-muted'}`}
                style={{ cursor: 'pointer', border: 'none' }}
                onClick={() => {
                  if (d === 30 && !settings.is_premium) { alert('Upgrade to Pro for 30-day SNR history.'); return }
                  setChartRange(d)
                }}>
                {d}d{d === 30 && !settings.is_premium ? ' 🔒' : ''}
              </button>
            ))}
          </div>
        </div>
        <SNRChart data={chartData} />
      </div>

      {/* AI Coach */}
      <div className="card" style={{ marginBottom: 'var(--xl)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sm)', marginBottom: 'var(--md)' }}>
          <Zap size={16} color="var(--accent)" />
          <h2 className="title text-primary">AI Coach</h2>
        </div>
        {coachLoading
          ? <p className="body-md text-muted animate-pulse">Analyzing your day…</p>
          : coachNote
            ? <p className="body-md text-primary" style={{ fontStyle: 'italic', lineHeight: 1.6, marginBottom: 'var(--md)' }}>"{coachNote}"</p>
            : <p className="body-md text-muted" style={{ marginBottom: 'var(--md)' }}>Get a personalized coaching note based on your signal/noise patterns.</p>
        }
        <button className="btn btn-outline" onClick={handleGenerateCoaching} disabled={coachLoading} style={{ fontSize: 13 }}>
          {coachLoading ? <span className="spinner" /> : null}
          {coachLoading ? 'Analyzing…' : coachNote ? '↺ Regenerate' : '▶ Generate Coaching Note'}
        </button>
      </div>

      {/* Daily Reflection */}
      <div className="card" style={{ marginBottom: 'var(--lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sm)' }}>
          <span className="label text-muted">Daily Reflection</span>
          {reflectionStreak >= 2 && (
            <div className="badge badge-accent" style={{ gap: 4 }}>
              <Flame size={10} /> {reflectionStreak}-day streak
            </div>
          )}
        </div>
        {question && <h2 className="title text-primary" style={{ marginBottom: 'var(--md)', lineHeight: 1.45 }}>{question}</h2>}
        <textarea
          id="reflection-input"
          className="input textarea"
          value={reflection}
          onChange={e => setReflection(e.target.value)}
          placeholder="Write it here…"
          rows={4}
          style={{ marginBottom: 'var(--md)' }}
        />
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <span className="spinner" /> : null}
          {saving ? 'Saving…' : 'Save Review ✓'}
        </button>
      </div>

      {/* Noise pattern nudge */}
      {noisePattern && (
        <div className="card animate-fade-slide-up" style={{ marginBottom: 'var(--xl)', borderLeft: '3px solid var(--amber)', display: 'flex', gap: 'var(--sm)' }}>
          <AlertTriangle size={18} color="var(--amber)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="label" style={{ color: 'var(--amber)', marginBottom: 'var(--xs)' }}>Pattern Detected</p>
            <p className="body-md text-primary" style={{ lineHeight: 1.5 }}>{noisePattern}</p>
          </div>
        </div>
      )}

      {/* Week in Review (Pro) */}
      {settings.is_premium && (
        <div className="card" style={{ marginBottom: 'var(--xl)', borderLeft: '3px solid var(--accent)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--md)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--xs)' }}>
              <Zap size={14} color="var(--accent)" />
              <span className="label text-accent">WEEK IN REVIEW</span>
            </div>
            <span className="label text-dim">w/c {getMonday(new Date())}</span>
          </div>
          {weekLoading
            ? <p className="body-md text-muted animate-pulse">Synthesising your week…</p>
            : weekSynth
              ? <p className="body-md text-primary" style={{ lineHeight: 1.6, marginBottom: 'var(--md)' }}>{weekSynth}</p>
              : <p className="body-md text-muted" style={{ marginBottom: 'var(--md)' }}>Your weekly synthesis will appear here.</p>
          }
          {!weekLoading && (
            <button className="btn btn-ghost" style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--xs)', color: 'var(--text-dim)', fontSize: 12 }}
              onClick={async () => {
                const { data: { user } } = await supabase.auth.getUser(); if (!user) return
                const ws = getMonday(new Date())
                await supabase.from('weekly_reviews').delete().eq('user_id', user.id).eq('week_start', ws)
                setWeekSynth(''); generateWeekReview(ws, user.id)
              }}>
              <RefreshCw size={12} /> Refresh
            </button>
          )}
        </div>
      )}

      <p className="label text-dim" style={{ textAlign: 'center', opacity: 0.4 }}>{todayKey()}</p>
    </div>
  )
}
